import {
  EditorText,
  GroupPostAttributesContentType,
  MediaTypePostEditorState,
  PostGroupsType
} from '../../../models/post-editor';
import {
  LinkDestination,
  LinkGoogleCallToAction,
  MediaProportions,
  MediaType,
  PostSource,
  Scalars,
  ThirdPartyPost,
  ThirdPartyPostMedia,
  ThirdPartyPostMediaType
} from '../../../models/graphql';
import { deserializeText } from '../TextEditor/helpers/serializer';
import updateImmutable from 'immutability-helper';
import { InitialPostEditorState } from '../states/initial';
import twttr from 'twitter-text';
import { BasePostLinkData } from '../../../hooks/usePostLinkSettings';
import { BaseLinkSettings } from '../TextEditor/helpers/onChange';
import { makeFinalLink } from '../states/reducers/link.reducer';
import { getUploadIdFromFileOrUrl } from '../../../helpers/media';
import { MediaUploadStatus } from '../../../constants/media';
import { getUrlParams } from '../../../helpers/getUrlParams';

//  TODO mentions related that we posponed
// const mapSocialProfileTypenameFromExternalProfile = (
//   externalProfile: ThirdPartyProfile
// ): CategoryFieldsFragment['defaultSocialProfiles'][number]['__typename'] => {
//   switch (externalProfile.platform) {
//     case 'facebook':
//       return 'FacebookSocialProfile';
//     default:
//       undefined;
//   }
// };

const getSharableMediaType = (media: ThirdPartyPostMedia[]) =>
  media.reduce((prev: ThirdPartyPostMediaType | null, currentValue) => {
    if (prev === null) return currentValue.type;
    return currentValue.type === prev ? prev : ThirdPartyPostMediaType.PHOTO;
  }, null);

const getInitialUrlFromPostText = (text: string, link: string): string => {
  const result = twttr.extractUrls(text);
  const mappedLink = result.reduce((prev, current) => {
    if (prev) return prev;
    if (current === link) return current;
    const trimmedLink = link
      .replace(/(^\w+:|^)\/\//, '')
      .replace(/\/$/, '')
      .trim();
    return trimmedLink === current ? current : '';
  }, '');
  return mappedLink ? mappedLink : link;
};

const getText = (post: ThirdPartyPost, baseLinkSettings: BasePostLinkData): EditorText => {
  const { text, linkUrl } = post; // textMentions, externalProfile removde since we don't do mentions for now
  // TODO this is a mention block that we postponed
  // const externalProfileTypename = mapSocialProfileTypenameFromExternalProfile(externalProfile);
  // const mentionType = getThirdPartyProfileSearchType(externalProfileTypename);
  // const baseMentions = post.textMentions.map<BaseMention>(mention => ({ ...mention, method }));
  // const mentions: BaseMention[] = textMentions.map(mention => ({
  //   externalId: mention.externalId,
  //   displayName: mention.start && mention.length ? text.substring(mention.start, mention.start + mention.length) : '',
  //   username: mention.start && mention.length ? text.substring(mention.start, mention.start + mention.length) : '',
  //   method: mentionType,
  //   ...(externalProfile.platform ? { platform: externalProfile.platform } : {})
  // }));
  //we should check for link
  const { defaultBitly, utmTracking, campaignTracking, shouldRemoveUtm } = baseLinkSettings;
  const linkSettings: BaseLinkSettings = {
    destination: LinkDestination.INITIAL,
    useBitly: !!defaultBitly,
    googleCallToAction: LinkGoogleCallToAction.NONE,
    utmTracking: utmTracking,
    addUtm: campaignTracking,
    removeUtm: shouldRemoveUtm,
    removePreview: false,
    linkMetadata: undefined
  };
  const validLinkUrl = linkUrl ? !!getUrlParams(linkUrl) : false;
  const link =
    linkUrl && validLinkUrl
      ? {
          ...linkSettings,
          initialUrl: getInitialUrlFromPostText(text, linkUrl),
          url: makeFinalLink({ ...linkSettings, initialUrl: linkUrl })
        }
      : null;
  const result = deserializeText(text, [], link, true); // TODO we don't do mentions for now
  return {
    id: 1,
    text: result
  };
};

export const getLink = (
  post: ThirdPartyPost,
  baseLinkSettings: BasePostLinkData
): GroupPostAttributesContentType['link'] => {
  const { text, linkUrl, media, postUrl } = post;
  const sharableMediaType = getSharableMediaType(media);
  const result = twttr.extractUrls(text);
  const { defaultBitly, utmTracking, campaignTracking, shouldRemoveUtm } = baseLinkSettings;
  const linkSettings: BaseLinkSettings = {
    destination: LinkDestination.INITIAL,
    useBitly: !!defaultBitly,
    googleCallToAction: LinkGoogleCallToAction.NONE,
    utmTracking: utmTracking,
    addUtm: campaignTracking,
    removeUtm: shouldRemoveUtm,
    removePreview: false,
    linkMetadata: undefined
  };
  if (sharableMediaType === ThirdPartyPostMediaType.VIDEO && postUrl) {
    const validUrl = !!getUrlParams(postUrl);
    return validUrl
      ? {
          ...linkSettings,
          initialUrl: getInitialUrlFromPostText(text, postUrl),
          url: makeFinalLink({ ...linkSettings, initialUrl: postUrl })
        }
      : null;
  }
  if (!linkUrl && !result.length) return null;
  if (linkUrl && !getUrlParams(linkUrl)) return null;
  const initialUrl = linkUrl || result[0];
  return {
    ...linkSettings,
    initialUrl,
    url: makeFinalLink({ ...linkSettings, initialUrl })
  };
};

type FilteredThirdPartyPostMedia = Omit<ThirdPartyPostMedia, 'url'> & { url: Scalars['String'] };

export const getMediaFromThirdPartyPost = (post: ThirdPartyPost): MediaTypePostEditorState[] => {
  const sharableMediaType = getSharableMediaType(post.media);
  let filteredMedia: FilteredThirdPartyPostMedia[] = [];
  // if we have video only posts, we should atach a link to the video post and upload video thumbnails as media
  if (sharableMediaType === ThirdPartyPostMediaType.VIDEO) {
    const mediaThumbnail = post.media.find(media => media.thumbnailUrl && media.type === ThirdPartyPostMediaType.VIDEO);
    if (mediaThumbnail && mediaThumbnail.thumbnailUrl)
      filteredMedia = [
        ...filteredMedia,
        { ...mediaThumbnail, url: mediaThumbnail.thumbnailUrl, type: ThirdPartyPostMediaType.PHOTO }
      ];
  } else {
    filteredMedia = post.media.flatMap(item => {
      switch (item.type) {
        case ThirdPartyPostMediaType.VIDEO:
          return item.thumbnailUrl ? { ...item, url: item.thumbnailUrl, type: ThirdPartyPostMediaType.PHOTO } : [];
        case ThirdPartyPostMediaType.PHOTO:
        default:
          return item.url ? { ...item, url: item.url } : [];
      }
    });
  }
  return filteredMedia.map(media => {
    //we set useUrl here as the ID, since MediaItems that have that prefix will be sent as url and will be uploaded and parsed
    const id = getUploadIdFromFileOrUrl(media.url);
    return {
      id,
      type: MediaType.PHOTO,
      url: media.url,
      thumbnailUrl: media.url,
      __typename: 'Photo',
      proportions: MediaProportions.SQUARE,
      width: 0,
      height: 0,
      filesize: 0,
      loading: true,
      status: MediaUploadStatus.UPLOADING,
      gif: false,
      length: 0,
      transcoded: false,
      externalUpload: true
    };
  });
};

export const mapThirdPartyPostToState = (
  thirdPartyPosts: ThirdPartyPost[],
  baseLinkSettings: BasePostLinkData,
  postSource: PostSource
): PostGroupsType[] =>
  thirdPartyPosts.map(thirdPartyPost => {
    return updateImmutable(InitialPostEditorState().postGroups[0], {
      groupPostAttributes: {
        postSource: {
          $set: postSource
        },
        content: {
          texts: {
            $set: [getText(thirdPartyPost, baseLinkSettings)]
          },
          link: { $set: getLink(thirdPartyPost, baseLinkSettings) },
          media: { $set: getMediaFromThirdPartyPost(thirdPartyPost) }
        }
      },
      expanded: { $set: false }
    });
  });
